import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cardRepaymentApi, verifyRepaymentApi, walletRepaymentApi } from '../../apis/payment';
import { handleApiError } from '../../helpers';
import { LoanRepayment, LoanStatusId } from '../../interfaces';
import { CardRepaymentDto, VerifyRepaymentDto, WalletRepaymentDto } from '../../interfaces/payment';
import { fetchLoanRepayments, updateLoanStatus } from '../../states/action_creators/loan.action_creator';
import { RootState } from '../../states/reducers';

export const useRepayLoan = () => {
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const { loanRepayments } = useSelector((state: RootState) => state.loanRepayment);
  const dispatch = useDispatch();

  const resetState = () => {
    setError(null);
    setSuccess(false);
    setProcessing(false);
  };

  const handleVerifyRepayment = async (payload: VerifyRepaymentDto) => {
    setError(null);
    setProcessing(true);
    try {
      await verifyRepaymentApi(payload);
      setProcessing(false);
      setSuccess(true);
      updateLoanRepaymentAsPaid(payload.repayments, loanRepayments);
    } catch (error) {
      setError(handleApiError(error));
      setProcessing(false);
    }
  };

  const handleCardRepayment = async (payload: CardRepaymentDto) => {
    setError(null);
    setProcessing(true);
    try {
      await cardRepaymentApi(payload);
      setProcessing(false);
      setSuccess(true);
      updateLoanRepaymentAsPaid(payload.repayments, loanRepayments);
    } catch (error) {
      setError(handleApiError(error));
      setProcessing(false);
    }
  };

  const handleWalletRepayment = async (payload: WalletRepaymentDto) => {
    setError(null);
    setProcessing(true);
    try {
      await walletRepaymentApi(payload);
      setProcessing(false);
      setSuccess(true);
      updateLoanRepaymentAsPaid(payload.repayments, loanRepayments);
    } catch (error) {
      setError(handleApiError(error));
      setProcessing(false);
    }
  };

  const updateLoanRepaymentAsPaid = async (repayments: number, loanRepayments: LoanRepayment[]) => {
    const repaymentsTobeUpdated = loanRepayments
      .filter((repayment) => !repayment.paid)
      .slice(0, repayments)
      .map((repayment) => repayment.id);

    const updatedRepayments = loanRepayments.map((repayment) => (repaymentsTobeUpdated.includes(repayment.id) ? { ...repayment, paid: true, updated_at: new Date(Date.now()).toString() } : repayment));

    dispatch(fetchLoanRepayments(updatedRepayments));

    closeLoanOnCompleteRepayment(updatedRepayments);
  };

  const closeLoanOnCompleteRepayment = (repayments: LoanRepayment[]) => {
    const unpaidRepayment = repayments.find((repayment) => !repayment.paid);
    if (!unpaidRepayment) {
      dispatch(updateLoanStatus(LoanStatusId.CLOSED));
    }
  };

  return { processing, error, success, handleVerifyRepayment, handleCardRepayment, handleWalletRepayment, resetState };
};
